<template>
  <div
    :id="`${source}_priority_tag_provider`"
    :class="[{ 'px-3': row }, 'inline-flex items-center']"
    v-if="statusText"
  >
    <div
      class="flex flex-row gap-1 items-center justify-center rounded-3xl px-2 py-[2px] w-fit h-5"
      :style="{
        backgroundColor: computedBackgroundColor,
        border: `1px solid ${computedBorderColor}`,
      }"
    >
      <img class="size-[11px]" :src="computedIcon" alt="priority_icon" />
      <span
        class="text-[11px] font-semibold"
        :style="{ color: computedTextColor }"
        >{{ statusText }}</span
      >
    </div>
  </div>
</template>

<script setup>
import {
  priorityTagProviderColorMapping,
  priorityTextColorMapping,
  priorityBackgroundColorMap,
  priorityTagProviderColors,
  priorityBorderColorMap,
  priorityTextColorMap,
  priorityIconMap,
} from "@/src/constants/customComponentConstants.js";
import { computed } from "vue";
import imagePath from "@/src/paths/imagePath.js";
import { toTitleCase } from "@/src/utils/utils.js";

const props = defineProps({
  source: { type: String, default: "custom_table" },
  row: { type: Object, default: null },
  text: { type: String, default: "" },
  backgroundColor: {
    type: String,
    default: null,
    validator: (value) =>
      value === null ||
      Object.values(priorityTagProviderColorMapping).includes(value),
  },
  textColor: {
    type: String,
    default: null,
    validator: (value) =>
      value === null ||
      Object.values(priorityTagProviderColorMapping).includes(value),
  },
  borderColor: {
    type: String,
    default: null,
    validator: (value) =>
      value === null ||
      Object.values(priorityTagProviderColorMapping).includes(value),
  },
  icon: {
    type: String,
    default: null,
  },
});

const styleMapByType = {
  background: priorityBackgroundColorMap,
  border: priorityBorderColorMap,
  text: priorityTextColorMap,
  icon: priorityIconMap,
};

const defaultStyles = {
  background: priorityTagProviderColors.GREY,
  border: priorityTagProviderColors.GREY,
  text: priorityTagProviderColors.WHITE,
  icon: imagePath.neutral.info.whiteDot,
};

const statusText = computed(() =>
  toTitleCase(props.row?.score || props.text || ""),
);

const computedBackgroundColor = computed(() => {
  const style =
    props.backgroundColor || getStyleForType(statusText.value, "background");
  return priorityTagProviderColorMapping[style];
});

const computedBorderColor = computed(() => {
  const style =
    props.borderColor || getStyleForType(statusText.value, "border");
  return priorityTagProviderColorMapping[style];
});

const computedTextColor = computed(() => {
  const style = props.textColor || getStyleForType(statusText.value, "text");
  return priorityTagProviderColorMapping[style];
});

const computedIcon = computed(() => {
  return props.icon || getStyleForType(statusText.value, "icon");
});

const normalizeText = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[_-\s]/g, "");

const getStyleForType = (text, type) => {
  try {
    const normalizedInput = normalizeText(text);

    const matchedPriority = Object.entries(priorityTextColorMapping).find(
      ([_, variations]) =>
        variations.some(
          (variant) => normalizeText(variant) === normalizedInput,
        ),
    )?.[0];

    return matchedPriority
      ? styleMapByType[type]?.[matchedPriority]
      : defaultStyles[type];
  } catch (error) {
    console.error("An error occurred while getting style for type:", error);
    return defaultStyles[type];
  }
};
</script>
